import type { AppNotification } from '../types/notifications';

export interface ResolvedNotificationDestination {
  href: string;
  label: string;
  supported: boolean;
}

type ResolveInput = Pick<AppNotification, 'type' | 'data' | 'sender' | 'sender_id'>;

const DEFAULT_DESTINATION: ResolvedNotificationDestination = {
  href: '/notifications',
  label: 'Open Notifications',
  supported: false,
};

const buildPath = (pathname: string, searchParams?: Record<string, string | null | undefined>) => {
  if (!searchParams) return pathname;

  const query = new URLSearchParams();
  Object.entries(searchParams).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim().length > 0) {
      query.set(key, value.trim());
    }
  });

  const search = query.toString();
  return search ? `${pathname}?${search}` : pathname;
};

const asString = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const getGroupId = (data: Record<string, any>) => asString(data.groupId || data.group_id);
const getGroupName = (data: Record<string, any>) => asString(data.groupName || data.group_name);
const getPostId = (data: Record<string, any>) => asString(data.postId || data.groupPostId || data.post_id);
const getCommentId = (data: Record<string, any>) => asString(data.commentId || data.comment_id);
const getChatId = (data: Record<string, any>) => asString(data.chatId);
const getEventId = (data: Record<string, any>) => asString(data.eventId || data.event_id || data.groupEventId);
const getRequestId = (data: Record<string, any>) =>
  asString(data.requestId || data.request_id || data.facility_request_id || data.rideRequestId || data.ride_request_id);

const resolveDestinationAlias = (destination: string, data: Record<string, any>): ResolvedNotificationDestination | null => {
  const groupId = getGroupId(data);

  switch (destination) {
    case 'notifications':
      return { href: '/notifications', label: 'Open Notifications', supported: true };
    case 'home_announcements':
      return { href: '/announcements', label: 'View Announcements', supported: true };
    case 'events':
      return {
        href: buildPath('/events', {
          requestId: getRequestId(data),
          eventId: getEventId(data),
        }),
        label: 'Open Events',
        supported: true,
      };
    case 'transportation':
    case 'request_ride':
      return {
        href: buildPath('/notifications', {
          transportation: '1',
          requestId: getRequestId(data),
          status: asString(data.status),
        }),
        label: 'View Notification',
        supported: false,
      };
    case 'social':
    case 'social_occasions':
      return { href: '/', label: 'Open Community', supported: true };
    case 'kids_church':
      return { href: '/kids-church', label: 'Open Kids Church', supported: true };
    case 'find_your_fit':
      return { href: '/volunteers', label: 'Open Volunteers', supported: true };
    case 'become_partner':
      return { href: '/new-partner', label: 'Open Partner Sign Up', supported: true };
    case 'businesses':
      return { href: '/businesses', label: 'View Businesses', supported: true };
    case 'book_store':
      return { href: '/admin/website/store', label: 'Open Store', supported: true };
    case 'service_planner':
      return {
        href: '/church-planner/my-assignments',
        label: 'Confirm Availability',
        supported: true,
      };
    case 'group_join_requests':
      if (groupId) {
        return { href: `/groups/${groupId}/manage`, label: 'Review Request', supported: true };
      }
      return DEFAULT_DESTINATION;
    default:
      return null;
  }
};

export const resolveNotificationDestination = (input: ResolveInput): ResolvedNotificationDestination => {
  const type = asString(input.type).toLowerCase();
  const data = (input.data as Record<string, any> | null) ?? {};
  const destination = asString(data.destination).toLowerCase();
  const groupId = getGroupId(data);
  const groupName = getGroupName(data);
  const postId = getPostId(data);
  const commentId = getCommentId(data);
  const chatId = getChatId(data);
  const eventId = getEventId(data);
  const requestId = getRequestId(data);
  const senderUserId = asString(data.targetUserId || data.senderId || data.sender_id || input.sender?.id || input.sender_id);
  const invitationStatus = asString(data.status).toLowerCase();

  const destinationAlias = destination ? resolveDestinationAlias(destination, data) : null;
  if (destinationAlias) {
    return destinationAlias;
  }

  if (data.isGroupPost === true || data.isGroupPostComment === true || type === 'group_post') {
    if (postId && groupId) {
      return {
        href: buildPath(`/groups/${groupId}/posts/${postId}`, { commentId }),
        label: 'View Group Post',
        supported: true,
      };
    }
    if (postId) {
      return {
        href: buildPath(`/community/posts/${postId}`, { commentId }),
        label: 'View Post',
        supported: true,
      };
    }
  }

  if (data.isGroupChatMessage === true && chatId) {
    if (groupId) {
      return {
        href: `/groups/${groupId}/chats/${chatId}`,
        label: 'Open Chat',
        supported: true,
      };
    }

    return {
      href: '/groups',
      label: 'Open Groups',
      supported: false,
    };
  }

  if (data.isGroupEvent === true && groupId) {
    return {
      href: buildPath(`/groups/${groupId}`, { tab: 'about', eventId, groupName }),
      label: 'Open Group',
      supported: true,
    };
  }

  switch (type) {
    case 'announcement':
      return { href: '/announcements', label: 'View Announcements', supported: true };
    case 'like':
    case 'comment':
    case 'prayer':
      if (postId) {
        return {
          href: buildPath(`/community/posts/${postId}`, { commentId }),
          label: type === 'prayer' ? 'View Prayer' : 'View Post',
          supported: true,
        };
      }
      return { href: '/', label: 'Open Community', supported: true };
    case 'mention':
      if (postId && groupId) {
        return {
          href: buildPath(`/groups/${groupId}/posts/${postId}`, { commentId }),
          label: 'View Mention',
          supported: true,
        };
      }
      if (postId) {
        return {
          href: buildPath(`/community/posts/${postId}`, { commentId }),
          label: 'View Mention',
          supported: true,
        };
      }
      return { href: '/', label: 'Open Community', supported: true };
    case 'event_reminder':
      if (eventId) {
        return {
          href: buildPath('/events', { eventId }),
          label: 'Open Event',
          supported: true,
        };
      }
      return { href: '/events', label: 'Open Events', supported: true };
    case 'event_request':
      return {
        href: buildPath('/events', { tab: 'pending', requestId }),
        label: 'Review Submission',
        supported: true,
      };
    case 'facility_request':
      return {
        href: buildPath('/facilities', { tab: 'requests', requestId }),
        label: 'Review Request',
        supported: true,
      };
    case 'group_join_request':
      if (groupId && invitationStatus === 'accepted') {
        return {
          href: `/groups/${groupId}`,
          label: 'View Group',
          supported: true,
        };
      }
      if (groupId) {
        return {
          href: `/groups/${groupId}/manage`,
          label: 'Review Request',
          supported: true,
        };
      }
      return DEFAULT_DESTINATION;
    case 'verification_request':
      return {
        href: '/super-admin',
        label: 'Review Request',
        supported: true,
      };
    case 'verification_response':
      return {
        href: '/profile',
        label: 'View Profile',
        supported: true,
      };
    case 'report':
      return {
        href: '/super-admin',
        label: 'Open Moderation',
        supported: true,
      };
    case 'wall_post':
      return {
        href: '/profile',
        label: 'View Profile',
        supported: true,
      };
    case 'hug':
    case 'dap':
      if (senderUserId) {
        return {
          href: `/members/${senderUserId}`,
          label: 'View Member',
          supported: true,
        };
      }
      return {
        href: '/profile',
        label: 'View Profile',
        supported: true,
      };
    case 'group_detail':
      if (groupId) {
        return {
          href: `/groups/${groupId}`,
          label: 'Open Group',
          supported: true,
        };
      }
      return { href: '/groups', label: 'Open Groups', supported: true };
    case 'ride_request':
    case 'ride_update':
    case 'ride_message':
      return {
        href: buildPath('/notifications', {
          transportation: '1',
          requestId,
          status: asString(data.status),
        }),
        label: 'View Notification',
        supported: false,
      };
    case 'message':
    case 'direct_message':
    case 'dm':
      if (senderUserId) {
        return {
          href: buildPath('/admin/users/messages', { recipient: senderUserId }),
          label: 'Open Messages',
          supported: true,
        };
      }
      return {
        href: '/admin/users/messages',
        label: 'Open Messages',
        supported: true,
      };
    case 'assignment_submission': {
      const submissionId = asString(data.submissionId) || asString(data.submission_id);
      if (submissionId) {
        return {
          href: `/classroom-teacher/grades/${encodeURIComponent(submissionId)}`,
          label: 'Open Submission',
          supported: true,
        };
      }
      return {
        href: '/classroom-teacher/grades',
        label: 'Open Grades',
        supported: true,
      };
    }
    case 'assignment_published': {
      // Students open assignments in the mobile app; web has no student classroom surface.
      return {
        href: '/notifications',
        label: 'View Assignment',
        supported: true,
      };
    }
    case 'service_plan_assignment':
      return {
        href: '/church-planner/my-assignments',
        label: 'Confirm Availability',
        supported: true,
      };
    default:
      return DEFAULT_DESTINATION;
  }
};

export const getNotificationActionLabel = (notification: ResolveInput) =>
  resolveNotificationDestination(notification).label;
